import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

/* The page a veterinary clinic lands on from the one time link in our email.
   No login, no account. It opens already filled in with everything PawLinx knows, so the
   clinic only supplies the prescriber details, the directions, and a decision. */

const L = "block text-[12.5px] text-[#333] mb-1";
const IN = "w-full h-9 rounded-[3px] border border-[#9aa7c4] px-2.5 text-[13px] text-[#0B2747] bg-white focus:outline-none focus:ring-2 focus:ring-[#0F62D6]/30";
const RO = "w-full min-h-9 rounded-[3px] border border-[#9aa7c4] px-2.5 py-2 text-[13px] text-[#0B2747] bg-[#EEF2FB]";

function Sec({ children }) {
  return <h2 className="text-center font-bold text-[17px] text-[#0B2747] mt-7 mb-1" style={{ fontFamily: "Georgia, serif" }}>{children}</h2>;
}
function Hint({ children }) {
  return <p className="text-center text-[12px] text-[#8A968F] mb-3">{children}</p>;
}
function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F4F1EA] py-8 px-3">
      <div className="max-w-[760px] mx-auto bg-white border border-[#E5DECE] rounded-xl p-5 sm:p-8">
        <img src="/pawlinx-logo-nav.png" alt="PawLinx" className="h-9 w-auto" />
        {children}
      </div>
      <p className="max-w-[760px] mx-auto text-center text-[11px] text-[#8A968F] mt-4 leading-relaxed">
        PAWLINX MED INC. &middot; 1-888-827-2717 &middot; support@pawlinx.com
      </p>
    </div>
  );
}

export default function RxAuthorizationPage() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    prescriber_name: "", prescriber_license: "", prescriber_phone: "", prescriber_fax: "",
    assistant: "", medication: "", quantity: "", sig: "", refills: "",
    decision: "", decline_reason: "",
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/rx/${token}`);
        if (!alive) return;
        setData(data);
        setState(data.state);
        if (data.state === "open") {
          setF((p) => ({ ...p, medication: data.medication || "", quantity: data.quantity_label || "" }));
        }
      } catch (e) {
        if (alive) { setErr(formatApiError(e)); setState("invalid"); }
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.decision) { setErr("Please choose whether you authorize this refill."); return; }
    if (f.decision !== "declined" && !f.prescriber_name.trim()) {
      setErr("Please enter the prescriber's name."); return;
    }
    setSaving(true);
    try {
      await api.post(`/rx/${token}`, f);
      setState("done");
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  if (state === "loading") {
    return <Shell><div className="py-20 text-center text-[#667085]"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />Loading this request</div></Shell>;
  }
  if (state === "invalid") {
    return <Shell><div className="py-14 text-center"><AlertTriangle className="w-8 h-8 text-[#BC4749] mx-auto mb-3" />
      <h1 className="text-[20px] font-bold text-[#0B2747] mb-2">This link is not valid</h1>
      <p className="text-[14px] text-[#667085] leading-relaxed">{err || "It may have been mistyped."} If you were sent this by PawLinx, call us on 1-888-827-2717 and we will send a fresh one.</p></div></Shell>;
  }
  if (state === "expired") {
    return <Shell><div className="py-14 text-center"><Clock className="w-8 h-8 text-[#EBB13E] mx-auto mb-3" />
      <h1 className="text-[20px] font-bold text-[#0B2747] mb-2">This request has expired</h1>
      <p className="text-[14px] text-[#667085] leading-relaxed">Links stay live for 14 days. Call us on 1-888-827-2717 or reply to our email and we will send a new one.</p></div></Shell>;
  }
  if (state === "already_done" || state === "done") {
    return <Shell><div className="py-14 text-center"><CheckCircle2 className="w-9 h-9 text-[#0E9F6E] mx-auto mb-3" />
      <h1 className="text-[20px] font-bold text-[#0B2747] mb-2">Thank you, we have your response</h1>
      <p className="text-[14px] text-[#667085] leading-relaxed max-w-[420px] mx-auto">
        This request is now closed and the link will not open again. We have let your client know.
        Nothing further is needed from your clinic.</p>
      <p className="text-[13px] text-[#8A968F] mt-4">Any questions, call us on 1-888-827-2717.</p>
    </div></Shell>;
  }

  const d = data || {};
  const declined = f.decision === "declined";

  return (
    <Shell>
      <h1 className="text-center font-bold text-[22px] sm:text-[26px] text-[#0B2747] mt-3 mb-1" style={{ fontFamily: "Georgia, serif" }}>
        Prescription Authorization Intake Form
      </h1>
      <p className="text-center text-[12.5px] text-[#8A968F] mb-1">
        One of your clients reached out to PawLinx about a refill. This link works once and expires 14 days after it was sent.
      </p>

      <form onSubmit={submit} data-testid="rx-auth-form">
        <Sec>Patient Info</Sec>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2"><label className={L}>Owner</label><div className={RO}>{d.client || "-"}</div></div>
          <div><label className={L}>Date</label><div className={RO}>{new Date().toLocaleDateString()}</div></div>
          <div><label className={L}>Patient Name</label><div className={RO}>{d.pet_name || "-"}</div></div>
          <div><label className={L}>Date Of Birth</label><div className={RO}>{d.pet_dob || "-"}</div></div>
          <div><label className={L}>Order</label><div className={RO}>{d.order_ref || "-"}</div></div>
          <div className="sm:col-span-3"><label className={L}>Other Info</label>
            <div className={RO}>{[d.pet_type, d.client_phone, d.client_email].filter(Boolean).join("  ·  ") || "-"}</div></div>
        </div>

        <Sec>Prescriber Info</Sec>
        <Hint>Your clinic completes this part.</Hint>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2"><label className={L}>Prescriber Name</label>
            <input className={IN} value={f.prescriber_name} onChange={set("prescriber_name")} placeholder="Dr." data-testid="rx-prescriber-name" /></div>
          <div><label className={L}>License #</label><input className={IN} value={f.prescriber_license} onChange={set("prescriber_license")} /></div>
          <div><label className={L}>Phone</label><input className={IN} value={f.prescriber_phone} onChange={set("prescriber_phone")} /></div>
          <div><label className={L}>Assistant</label><input className={IN} value={f.assistant} onChange={set("assistant")} placeholder="Name and role" /></div>
          <div><label className={L}>Fax</label><input className={IN} value={f.prescriber_fax} onChange={set("prescriber_fax")} /></div>
        </div>

        <Sec>Medication Info</Sec>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2"><label className={L}>Medication</label>
            <input className={IN} value={f.medication} onChange={set("medication")} data-testid="rx-medication" /></div>
          <div><label className={L}>Quantity</label><input className={IN} value={f.quantity} onChange={set("quantity")} /></div>
          <div className="sm:col-span-2"><label className={L}>Sig, the directions</label>
            <textarea className={`${IN} h-[70px] py-2 resize-none`} value={f.sig} onChange={set("sig")}
              placeholder="For example: administer 2 puffs via a spacer every 12 hours" data-testid="rx-sig" /></div>
          <div><label className={L}>Refills</label><input className={IN} value={f.refills} onChange={set("refills")} /></div>
        </div>
        <p className="text-[12px] text-[#8A968F] mt-1.5 leading-relaxed">
          Please change the medication, quantity or directions above if what was requested is not what you authorize.
          PawLinx dispenses at most a three month supply in one order.
        </p>

        <Sec>Authorization</Sec>
        <div className="space-y-2 mt-2">
          {[["authorized", "I authorize this refill as written above"],
            ["authorized_amended", "I authorize it with the changes I have made above"],
            ["declined", "I do not authorize this refill"]].map(([v, label]) => (
            <label key={v} className={`flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors ${f.decision === v ? "border-[#0F62D6] bg-[#F3F7FE]" : "border-[#E5DECE] hover:border-[#CBDFF9]"}`}>
              <input type="radio" name="decision" value={v} checked={f.decision === v}
                onChange={() => setF((p) => ({ ...p, decision: v }))}
                className="mt-0.5 w-4 h-4 accent-[#0F62D6]" data-testid={`rx-decision-${v}`} />
              <span className="text-[13.5px] text-[#0B2747]">{label}</span>
            </label>
          ))}
        </div>
        {declined && (
          <div className="mt-3"><label className={L}>Reason, if you would like the client to know</label>
            <input className={IN} value={f.decline_reason} onChange={set("decline_reason")}
              placeholder="For example: a check up is needed before the next refill" data-testid="rx-decline-reason" />
            <p className="text-[12px] text-[#8A968F] mt-1.5">This is passed on to your client word for word.</p></div>
        )}

        {(d.technician_name || d.technician_license) && (
          <>
            <Sec>Pharmacy Technician Info</Sec>
            <Hint>Completed by PawLinx once your authorization is received.</Hint>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2"><label className={L}>Pharmacy Technician</label><div className={RO}>{d.technician_name || "-"}</div></div>
              <div><label className={L}>License #</label><div className={RO}>{d.technician_license || "-"}</div></div>
            </div>
          </>
        )}

        {err && <div className="mt-5 rounded-lg bg-[#BC4749]/10 border border-[#BC4749]/25 px-4 py-3 text-[13.5px] text-[#BC4749]" data-testid="rx-error">{err}</div>}

        <button type="submit" disabled={saving}
          className="mt-6 w-full h-12 rounded-xl bg-[#0F62D6] hover:bg-[#0A4FB4] disabled:opacity-60 text-white font-semibold text-[15px] flex items-center justify-center gap-2 transition-colors"
          data-testid="rx-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit to PawLinx"}
        </button>

        <p className="text-[11.5px] text-[#8A968F] mt-4 leading-relaxed">
          PAWLINX MED INC. is a pet medication referral and coordination service. PawLinx is not a pharmacy and
          does not prescribe. Dispensing is carried out by a licensed pharmacy partner. If you believe this reached
          you in error, call 1-888-827-2717 and we will remove the request.
        </p>
      </form>
    </Shell>
  );
}
